// ════════════════════════════════════════════════════════════════════════
//  Wörter pro Minute – vorher, nachher, Zuwachs
//
//  Reine Rechnung: die Messungen hängen an einem Testschlüssel und wissen nichts
//  von Lehrgängen; die Zuordnung entsteht erst hier, gegen das Bundle der
//  Fassung, die diese Klasse übt.
//
//  Gerundet wird ERST HIER. In der Datenbank stehen Zeichen und Sekunden – eine
//  gespeicherte Zahl „14“ liesse sich später nicht mehr anders ausdrücken.
// ════════════════════════════════════════════════════════════════════════

import { wordsPerMinute, type Bundle } from "@otto/lehrgang";
import type { StudentRow } from "./data.js";

export type Speed = {
  readonly before: number | null;
  readonly after: number | null;
  /** Nur wenn beide Werte da sind – ein Zuwachs gegen nichts ist keiner. */
  readonly gain: number | null;
};

const round = (chars: number, seconds: number) =>
  Math.round(wordsPerMinute({ chars, seconds }));

/** Was dieses Kind in DIESEM Lehrgang gemessen hat. */
export function speedOf(row: StudentRow, bundle: Bundle): Speed | null {
  const key = bundle.assessment?.key;
  if (!key) return null;

  const find = (phase: "vorher" | "nachher") =>
    row.tests.find((m) => m.testKey === key && m.phase === phase) ?? null;

  const vorher = find("vorher");
  const nachher = find("nachher");
  if (!vorher && !nachher) return null;

  const before = vorher ? round(vorher.chars, vorher.seconds) : null;
  const after = nachher ? round(nachher.chars, nachher.seconds) : null;
  return {
    before,
    after,
    gain: before !== null && after !== null ? after - before : null,
  };
}

/**
 * Anzeige einer Messung. Hier, weil beide Ansichten und die Klassenzeile
 * dieselbe Schreibweise brauchen – und weil „–“ für „nicht gemessen“ eine
 * Aussage ist, die nicht in drei Komponenten einzeln erfunden werden soll.
 */
export const showWpm = (value: number | null) => (value === null ? "–" : String(value));

export const showGain = (value: number | null) =>
  value === null ? "–" : value > 0 ? `+${value}` : String(value);

export type ClassSpeed = {
  /** Auf wie vielen Messungen der Mittelwert beruht. Ohne diese Zahl ist er eine Behauptung. */
  readonly beforeCount: number;
  readonly afterCount: number;
  readonly before: number | null;
  readonly after: number | null;
  /**
   * Der Zuwachs im Mittel – gerechnet NUR über Kinder mit beiden Werten.
   *
   * Die Differenz der beiden Mittelwerte wäre bequemer und falsch: sie mischte
   * Kinder, die nur vorher gemessen haben, mit solchen, die nur nachher gemessen
   * haben, und nennte das Ergebnis Fortschritt.
   */
  readonly gain: number | null;
  readonly gainCount: number;
};

const mean = (values: readonly number[]) =>
  values.length === 0
    ? null
    : Math.round((values.reduce((sum, v) => sum + v, 0) / values.length) * 10) / 10;

/** Die Zahl fürs Gespräch: was die Klasse vorher konnte, was nachher. */
export function classSpeed(rows: readonly StudentRow[], bundle: Bundle): ClassSpeed {
  const speeds = rows
    .map((row) => speedOf(row, bundle))
    .filter((s): s is Speed => s !== null);

  const before = speeds.map((s) => s.before).filter((v): v is number => v !== null);
  const after = speeds.map((s) => s.after).filter((v): v is number => v !== null);
  const gains = speeds.map((s) => s.gain).filter((v): v is number => v !== null);

  return {
    beforeCount: before.length,
    afterCount: after.length,
    before: mean(before),
    after: mean(after),
    gain: mean(gains),
    gainCount: gains.length,
  };
}
